import React, {
  ReactNode,
  RefObject,
  createContext,
  useContext,
  useRef,
  useState,
  Fragment,
  useEffect,
} from "react";
import Fullscreen from "../../layout/modal/fullscreen";
import { createPortal } from "react-dom";
import useBodyClass from "../../../shared/use_body_class";
import { useNavigate } from "react-router-dom";
import { KIND, SIZE } from "../../base_ui/inputs/button_menu";
import { AppContext } from "../../../app";
import AdminDashboardPageTitle from "../../base_ui/higher_order_components/page_header";
import { ArrowLeft } from "@carbon/icons-react";
import { Grid, Cell } from "baseui/layout-grid";
import {
  SinglePageOverlay,
  AdminDashboardHeader,
  AdminDashboardContainer,
  ParticipantDashboardPageTitle,
  ParticipantDashboardContainer,
  StyledBackButton,
} from "./shared";

const SinglePageContext = createContext<RefObject<HTMLDivElement> | null>(null);

export function SinglePageProvider({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [currentRef, setRef] = useState<RefObject<HTMLDivElement> | null>(null);

  useEffect(() => {
    setRef(ref);
  }, []);

  return (
    <Fragment>
      <SinglePageContext.Provider value={currentRef}>{children}</SinglePageContext.Provider>
      <div ref={ref} />
    </Fragment>
  );
}

/**
 * When using this component and ensure it has a dedicated URL, you will need to
 * wrap this component in 'react-router-dom's <Route>. (Unfortunately), trying to
 * make this component more reusable by placing wrapping it within a route in this
 * file causes a scrolling bug.
 *
 * The 'backTo' prop should match
 * the URL of the parent page.
 */
export default function SinglePage({
  children,
  pageTitle,
  pageSubtitle,
  backToUrl,
  backToUrlCallback,
  backToText,
  className,
  dataTest,
}: {
  children: ReactNode;
  pageTitle: string;
  pageSubtitle?: string;
  backToUrl?: string;
  backToText: string;
  className?: string;
  dataTest?: string;
  backToUrlCallback?: () => void;
}) {
  useBodyClass("disable-scrolling");

  const pageRef = useContext(SinglePageContext);
  const { envBanner } = useContext(AppContext);
  const navigate = useNavigate();

  // There are quite a few design differences here between the Admin Dashboard
  // and the Participant Dashboard based on different designs and existing tech
  // debt between the two. The admin is displaying a completely different minimal
  // header whenever this component is rendered whereas the Participant Dashboard
  // uses the existing standard header. We are using the URL here to determine
  // which dashboard we are currently on.
  const fromAdminDashboard = window.location.href.includes("admin_dashboard");

  const containerContents = (
    <>
      <StyledBackButton
        buttonType={KIND.secondary}
        buttonSize={fromAdminDashboard ? SIZE.default : SIZE.compact}
        leftIcon={<ArrowLeft size={16} />}
        data-test={`${dataTest}-return-button`}
        onClick={() => {
          backToUrl && navigate(backToUrl);
          backToUrlCallback && backToUrlCallback();
        }}
        ariaLabel={`${backToText} ${pageSubtitle}`}
      >
        {backToText}
      </StyledBackButton>
      {children}
    </>
  );

  return pageRef && pageRef.current
    ? createPortal(
        <SinglePageOverlay className={className}>
          <div className="single-page-wrapper" id="single-page-main-content">
            <Fullscreen className="modal" data-test={dataTest} showEnvBanner={!!envBanner}>
              {fromAdminDashboard ? (
                <>
                  <AdminDashboardHeader />
                  <Grid>
                    <Cell span={12}>
                      <AdminDashboardContainer id="single-page-container">
                        <AdminDashboardPageTitle
                          title={pageTitle}
                          subtitle={pageSubtitle ?? ""}
                          dataTest={`${dataTest}-program-name`}
                        />
                        {containerContents}
                      </AdminDashboardContainer>
                    </Cell>
                  </Grid>
                </>
              ) : (
                <Grid gridGutters={0}>
                  <Cell span={[4, 8, 12]}>
                    <ParticipantDashboardContainer
                      id="single-page-container"
                      showEnvBanner={!!envBanner}
                    >
                      <ParticipantDashboardPageTitle
                        title={pageTitle}
                        subtitle={pageSubtitle ?? ""}
                        dataTest={`${dataTest}-program-name`}
                      />
                      {containerContents}
                    </ParticipantDashboardContainer>
                  </Cell>
                </Grid>
              )}
            </Fullscreen>
          </div>
        </SinglePageOverlay>,
        pageRef.current,
      )
    : null;
}
